import React from 'react';

import type { EditorView } from 'prosemirror-view';

import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import {
	areAttributionColorGatesEnabled,
	resolveContributors,
} from './pm-plugins/decorations/colorSchemes/attributions';
import { getDeletedWidgets } from './pm-plugins/getDeletedWidgets';
import { getScrollableDecorations } from './pm-plugins/getScrollableDecorations';
import { createPlugin, showDiffPluginKey } from './pm-plugins/main';
import { resolveDiffContributors } from './pm-plugins/resolveDiffContributors';
import type { ShowDiffParams, ShowDiffPlugin } from './showDiffPluginType';
import { IndicatorBarContentComponent } from './ui/IndicatorBar/IndicatorBarContentComponent';

const normalizeShowDiffParams = (params: ShowDiffParams | undefined) => {
	if (!params || !('stepsWithAttribution' in params)) {
		// Cleared so a previous attributed diff's contributors cannot leak onto an unattributed one.
		return params ? { ...params, stepAttributions: [], contributors: undefined } : params;
	}

	const { stepsWithAttribution, contributorProfiles, ...rest } = params;

	return {
		...rest,
		// Resolved and keyed at the public boundary so both the contributor model and the attribution-key
		// format stay internal.
		contributors: areAttributionColorGatesEnabled()
			? resolveContributors(resolveDiffContributors(stepsWithAttribution, contributorProfiles))
			: undefined,
		steps: stepsWithAttribution.map(({ step }) => step),
		stepAttributions: stepsWithAttribution.map(({ stepAttribution }) => stepAttribution),
	};
};

export const showDiffPlugin: ShowDiffPlugin = ({ api, config }) => {
	// Captured from the plugin's view lifecycle (see `createPlugin`) so read-only actions can access
	// the current state without exposing the plugin key to consumers.
	let editorView: EditorView | undefined;
	const setEditorView = (view: EditorView | undefined) => {
		editorView = view;
	};

	return {
		name: 'showDiff',
		actions: {
			getDeletedWidgets: (range) => (editorView ? getDeletedWidgets(editorView.state, range) : []),
		},
		commands: {
			showDiff:
				(params?: ShowDiffParams) =>
				({ tr }) => {
					if (expValEquals('platform_editor_diff_plugin_extended', 'isEnabled', true)) {
						api?.userIntent?.commands.setCurrentUserIntent('viewingDiff')({ tr });
					}
					return tr.setMeta(showDiffPluginKey, {
						...normalizeShowDiffParams(params),
						action: 'SHOW_DIFF',
					});
				},
			hideDiff: ({ tr }) => {
				if (expValEquals('platform_editor_diff_plugin_extended', 'isEnabled', true)) {
					api?.userIntent?.commands.setCurrentUserIntent('default')({ tr });
				}
				return tr.setMeta(showDiffPluginKey, {
					steps: [],
					stepAttributions: [],
					contributors: undefined,
					action: 'HIDE_DIFF',
				});
			},
			scrollToNext: ({ tr }) => {
				return tr.setMeta(showDiffPluginKey, { action: 'SCROLL_TO_NEXT' });
			},
			scrollToPrevious: ({ tr }) => {
				return tr.setMeta(showDiffPluginKey, { action: 'SCROLL_TO_PREVIOUS' });
			},
		},
		pmPlugins() {
			return [
				{
					name: 'showDiffPlugin',
					// Contributor tags draw themselves into their own decoration widgets — plain DOM, so
					// no portal provider is needed here.
					plugin: ({ getIntl }) => createPlugin(config, getIntl, api, setEditorView),
				},
			];
		},
		contentComponent: () => {
			if (!expValEquals('platform_editor_diff_plugin_extended', 'isEnabled', true)) {
				return null;
			}
			// Rendered here so it shares the DOM context of the decoration anchor spans. Contributor
			// tags are not: each one mounts into the host element its own decoration draws.
			return (
				<React.Fragment>
					<IndicatorBarContentComponent api={api} />
				</React.Fragment>
			);
		},
		getSharedState: (editorState: EditorState | undefined) => {
			if (!editorState) {
				return {
					isDisplayingChanges: false,
					activeIndex: undefined,
					...(expValEquals('platform_editor_diff_plugin_extended', 'isEnabled', true)
						? { diffDescriptors: [] }
						: {}),
				};
			}
			const pluginState = showDiffPluginKey.getState(editorState);
			const decorationCount = getScrollableDecorations(pluginState?.decorations, editorState.doc);
			return {
				isDisplayingChanges: decorationCount.length > 0,
				activeIndex: pluginState?.activeIndex,
				numberOfChanges: decorationCount.length,
				...(expValEquals('platform_editor_diff_plugin_extended', 'isEnabled', true)
					? {
							contributorTags: pluginState?.contributorTags,
							diffDescriptors: pluginState?.diffDescriptors,
							showIndicators: pluginState?.showIndicators,
						}
					: {}),
			};
		},
	};
};
